// src/utils/carteleria/ingresosERCarteleria.js
//
// F3d — registros de ingreso de Cartelería del ER v2 (Bloque B hardware, Bloque C gestión y contenidos) leídos del
// motor `calendarioNegocio`. Sirven a `registrosHardware` / `registrosGestion` de `src/pages/ERv2/ingresosER.js`
// SOLO para los meses >= CORTE_HISTORICO (2026-10); antes del corte manda el histórico congelado
// (`./historico/ingresosERCarteleriaHistorico.js`).
//
// El criterio del ER NO cambia (D11): con factura, el mes de la factura; sin factura, mes a mes. Lo único que cambia
// desde el corte es lo que viene del motor: fin de contrato (el servicio termina en su último mes; un activo vencido
// se prorroga hasta el mes actual, "el estado manda"), ventana de contenidos, D12 (tramo único sin monto vale
// `monto_hardware`) y el precio total del contrato (`total_contrato`, repartido por días).
//
// Conversión: la misma de hoy (UF/USD del día de la factura o fin de mes; Perú con el TC mensual `toPEN`), envuelta en
// el helper único `convertirMontoCarteleria` — mismos valores y mismo redondeo.
import { crearRegistroFinancieroER } from "../../pages/ERv2/registroFinancieroER";
import { calcularDolarMensualPromedio } from "../exchangeRateService";
import { parseLocalYMD } from "../fechasFuturosPanel";
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";
import {
  registrosGestion as registrosGestionAnterior,
  registrosHardware as registrosHardwareHistorico,
} from "./historico/ingresosERCarteleriaHistorico";

const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);
const monedaLocalDe = (pais) => (pais === "Chile" ? "CLP" : "PEN");

/**
 * Un negocio con precio TOTAL del contrato válido (`modo_precio_gestion = 'total_contrato'`, monto > 0, con fechas)
 * es de un modo que el código anterior no sabía representar (fee unitario 0 → no producía gestión): no tiene
 * "histórico" que congelar, así que TODOS sus meses salen del motor.
 */
export const esNegocioSoloMotor = (negocio, hoy) => {
  if (!negocio || negocio.modo_precio_gestion !== "total_contrato") return false;
  return calendarioNegocio(negocio, { hoy }).precioGestion.modo === "total_contrato";
};

// ─── HARDWARE (Bloque B) ────────────────────────────────────────────────────────────────────────────────────────

// Ventana OPEX del código anterior = `fecha_cierre` + (num_cuotas || plazo || 36) meses. El motor la calcula desde
// `fecha_inicio` + plazo. Solo se lee del motor cuando ambas arrancan en el mismo mes (por texto) y cuentan las mismas
// cuotas; si no, se sigue con la lógica anterior para ese negocio (mismo criterio que `calculateVentas`).
const ventanaOpexLeibleDelMotor = (n, cal) => {
  const plazo = parseInt(n.plazo_contrato_meses, 10) || 0;
  const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;
  return (
    plazo > 0 &&
    totalCuotas === plazo &&
    !!n.fecha_inicio &&
    String(n.fecha_cierre).slice(0, 7) === String(n.fecha_inicio).slice(0, 7) &&
    cal.devengo.some((d) => d.componente === "opex")
  );
};

export const registrosHardwareMotor = ({
  negocios,
  pais,
  ano,
  getUFValue,
  getDolarValue,
  costosGestion = null,
  hoy = new Date(),
}) => {
  const registros = [];
  const defaultMoneda = pais === "Chile" ? "UF" : "PEN";
  const local = monedaLocalDe(pais);

  (negocios || []).forEach((n) => {
    if (!n.incluye_hardware || n.eliminada) return;
    if (!["100%", "Activo"].includes(n.estado)) return;
    const moneda = (n.moneda_hardware || defaultMoneda).toUpperCase();
    const cal = calendarioNegocio(n, { hoy });
    let yaProcesadoConDesglose = false;

    // Misma conversión que hoy (UF solo Chile, USD/US$ con el dólar del país, resto tal cual), por el helper único.
    const convertir = (monto, fechaParaTC) =>
      convertirMontoCarteleria(
        (m, desde) => {
          if (desde === "UF" && pais === "Chile") return Math.round(m * getUFValue(fechaParaTC));
          if (desde === "US$" || desde === "USD") return Math.round(m * getDolarValue(fechaParaTC));
          return m;
        },
        monto,
        moneda,
        local
      );
    // P2.117-2 (PASO 7, fix, Chile solamente — Perú 10.265/10.267 sigue sin tocar): "sin fecha
    // propia" (CAPEX sin desglose / OPEX por cuota sin factura real) convertía USD/US$ con el TC
    // del ÚLTIMO día del mes — inconsistente con la convención ya decidida para el resto de la
    // reingeniería ("sin fecha propia → TC PROMEDIO real del mes", `calcularDolarMensualPromedio`,
    // Criterio 1b). Acá se aplica la MISMA convención, reusando la función (antes solo consumida
    // por la conversión LOCAL→US$ de display, `conversionER.js`) para esta conversión de ORIGEN
    // (USD→local) — primera vez que se usa para esto, documentado en el cierre. UF queda sin
    // tocar (no hay "UF promedio" definido en ningún punto de esta reingeniería — Chile hardware
    // es casi siempre UF, variación diaria mínima, decisión pendiente de Ignacio si corresponde).
    const dolarMensualPromedioChile = pais === "Chile" ? calcularDolarMensualPromedio("Chile", ano) : null;
    const convertirSinFecha = (monto, mesIndice, fechaParaTCFallback) => {
      if (pais !== "Chile" || (moneda !== "US$" && moneda !== "USD")) return convertir(monto, fechaParaTCFallback);
      const tc = dolarMensualPromedioChile[mesIndice] || 0;
      return convertirMontoCarteleria((m) => Math.round(m * tc), monto, moneda, local);
    };

    const push = (fecha, mesIndice, montoLocal, tramo) => {
      registros.push(
        crearRegistroFinancieroER({
          fecha,
          ano,
          mesIndice,
          montoLocal,
          bloque: "B",
          concepto: "ingreso_hardware",
          pais,
          meta: { id: n.id, origen: "negocios_carteleria", tramo },
        })
      );
    };

    // CAPEX con desglose de pagos (1 registro por tramo con factura). D12: un tramo ÚNICO sin monto vale
    // `monto_hardware` (`montoEfectivo` del motor).
    if (
      n.modalidad === "CAPEX" &&
      Array.isArray(n.detalle_pagos_hardware) &&
      n.detalle_pagos_hardware.some((p) => p.monto != null)
    ) {
      n.detalle_pagos_hardware.forEach((pago, i) => {
        const fechaTramo = pago.facturafecha || pago.fecha_factura;
        if (!fechaTramo || pago.monto == null) return;
        const fecha = fechaLocalDeNegocio(fechaTramo);
        const mes = fecha.getMonth();
        const anoTramo = fecha.getFullYear();
        if (anoTramo !== ano) return;
        const fechaUsada = String(fechaTramo).split("T")[0];
        const montoPago = Number(pago.monto);
        const monto = montoPago > 0 ? montoPago : cal.hardwareCapex.tramos[i].montoEfectivo;
        push(fechaUsada, mes, convertir(monto, fechaUsada), "capex_desglose");
      });
      yaProcesadoConDesglose = true;
    }

    // OTROS CAPEX (mismo patrón, tabla separada).
    if (n.modalidad === "CAPEX" && Array.isArray(n.detalle_otros_hardware) && n.detalle_otros_hardware.length > 0) {
      n.detalle_otros_hardware.forEach((pago) => {
        if (!pago.fecha_factura || pago.monto == null) return;
        const fecha = fechaLocalDeNegocio(pago.fecha_factura);
        const mes = fecha.getMonth();
        const anoTramo = fecha.getFullYear();
        if (anoTramo !== ano) return;
        const fechaUsada = String(pago.fecha_factura).split("T")[0];
        push(fechaUsada, mes, convertir(Number(pago.monto), fechaUsada), "capex_otros");
      });
    }

    // CAPEX sin desglose (fallback) / OPEX — mutuamente exclusivos, igual que antes.
    if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
      const fechaCierre = fechaLocalDeNegocio(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      const anoCierre = fechaCierre.getFullYear();
      if (anoCierre !== ano) return;
      const ultimoDia = new Date(ano, mesCierre + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];
      const montoLocal = convertirSinFecha(
        cal.hardwareCapex ? cal.hardwareCapex.monto : Number(n.monto_hardware) || 0,
        mesCierre,
        fechaStr
      );
      push(null, mesCierre, montoLocal, "capex_sin_desglose");
    } else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
      const pantallas = Number(n.cantidad_pantallas) || 1;
      const cuotaBase = Number(n.cuota_hardware_opex);

      // Modo "facturado real": meses_facturacion con entries tipo "opex" (documentos reales: no cambian).
      const mesesFact = Array.isArray(n.meses_facturacion) ? n.meses_facturacion : [];
      const opexEntries = mesesFact.filter((e) => e && e.tipo === "opex" && e.facturafecha);
      if (opexEntries.length > 0) {
        opexEntries.forEach((e) => {
          const [fy, fm] = e.facturafecha.split("-").map(Number);
          if (fy !== ano) return;
          const idx = fm - 1;
          if (idx < 0 || idx > 11) return;
          const montoLocal = convertir(cuotaBase * pantallas, e.facturafecha);
          push(String(e.facturafecha).slice(0, 10), idx, montoLocal, "opex_facturado_real");
        });
        return;
      }

      // Fallback por cuota: la ventana y la cuota salen del devengo `opex` del motor (el servicio termina en su
      // último mes; un activo vencido sigue hasta el mes actual). Si el motor no puede leerla, lógica anterior.
      if (!ventanaOpexLeibleDelMotor(n, cal)) {
        registrosHardwareHistorico({ negocios: [n], pais, ano, getUFValue, getDolarValue, costosGestion }).forEach(
          (r) => registros.push(r)
        );
        return;
      }
      cal.devengo
        .filter((d) => d.componente === "opex" && d.ano === ano)
        .forEach((d) => {
          const mesActual = d.mesNum - 1;
          let fechaPropia = null;
          let fechaParaTC;
          const ultimoDia = new Date(ano, mesActual + 1, 0);
          if (costosGestion) {
            const registro = costosGestion.find(
              (c) =>
                Number(c.negocio_id) === n.id &&
                Number(c.mes) === mesActual + 1 &&
                Number(c.ano) === ano &&
                c.fecha_factura
            );
            fechaParaTC = registro?.fecha_factura?.split("T")[0] || ultimoDia.toISOString().split("T")[0];
            fechaPropia = registro?.fecha_factura ? fechaParaTC : null;
          } else {
            fechaParaTC = ultimoDia.toISOString().split("T")[0];
          }
          const montoLocal = fechaPropia
            ? convertir(d.monto, fechaParaTC)
            : convertirSinFecha(d.monto, mesActual, fechaParaTC);
          push(fechaPropia, mesActual, montoLocal, "opex_por_cuota");
        });
    }
  });
  return registros;
};

// ─── GESTIÓN Y CONTENIDOS (Bloque C) ────────────────────────────────────────────────────────────────────────────

// Cuotas con factura real por mes (criterio del ER, D11: cada cuota va al mes de la FACTURA; varias cuotas del mismo
// mes de factura se suman). null si el año no tiene facturas de gestión/contenidos. `monto` (solo precio total del
// contrato) acumula el devengo del motor del mes de servicio de cada cuota facturada.
const cuotasFacturadasPorMes = (negocio, year, cal) => {
  const entries = Array.isArray(negocio.meses_facturacion) ? negocio.meses_facturacion : [];
  if (entries.length === 0) return null;
  const totalContrato = cal.precioGestion.modo === "total_contrato";
  const result = Array.from({ length: 12 }, () => ({ gestion: 0, contenidos: 0, fechaFactura: null, monto: 0 }));
  let hasAny = false;
  entries.forEach((e) => {
    if (!e || !e.facturafecha) return;
    if (e.tipo !== "gestion" && e.tipo !== "contenidos") return;
    const [fy, fm] = e.facturafecha.split("-").map(Number);
    if (fy !== year) return;
    const idx = fm - 1;
    if (idx < 0 || idx > 11) return;
    result[idx][e.tipo] += 1;
    result[idx].fechaFactura = e.facturafecha;
    if (totalContrato && e.tipo === "gestion") {
      const d = cal.devengo.find(
        (x) => x.componente === "gestion" && x.mes === `${e.ano}-${String(e.mes).padStart(2, "0")}`
      );
      result[idx].monto += d ? d.monto : 0;
    }
    hasAny = true;
  });
  if (negocio.incluye_contenidos) {
    const contInicio = negocio.contenidos_fecha_inicio ? new Date(negocio.contenidos_fecha_inicio + "T00:00:00") : null;
    const contFin = negocio.contenidos_fecha_fin ? new Date(negocio.contenidos_fecha_fin + "T00:00:00") : null;
    result.forEach((slot, idx) => {
      if (slot.gestion > 0 && slot.contenidos === 0) {
        const slotDate = new Date(year, idx, 1);
        const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
        if (enRango) slot.contenidos = slot.gestion;
      }
    });
  }
  return hasAny ? result : null;
};

export const registrosGestionMotor = ({
  negocios,
  pais,
  ano,
  convertirAMonedaLocal,
  dolarMensualPeru,
  dolarFallback,
  hoy = new Date(),
}) => {
  const registros = [];
  const local = monedaLocalDe(pais);

  const toPEN = (monto, moneda, mesIndice) => {
    if (!monto) return 0;
    const m = (moneda || "PEN").toUpperCase();
    if (m === "USD" || m === "US$") return monto * (dolarMensualPeru[mesIndice] || dolarFallback["Perú"]);
    return monto;
  };

  (negocios || []).forEach((negocio) => {
    if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
    const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
    if (plazoMeses === 0) return;
    const cal = calendarioNegocio(negocio, { hoy });
    const totalContrato = cal.precioGestion.modo === "total_contrato";
    // El motor no devenga gestión/contenidos de un OT (fee 0): el ER de antes sí la cuenta con fee: lógica anterior.
    const otConFee =
      negocio.tipo_vertical === "OT" &&
      ((parseFloat(negocio.fee_gestion_unitario) || 0) > 0 ||
        (negocio.incluye_contenidos && (parseFloat(negocio.fee_contenidos_unitario) || 0) > 0));
    if (otConFee && !totalContrato) {
      registrosGestionAnterior({
        negocios: [negocio],
        pais,
        ano,
        convertirAMonedaLocal,
        dolarMensualPeru,
        dolarFallback,
      }).forEach((r) => registros.push(r));
      return;
    }
    const gestionMensual = cal.mensual.gestion.monto;
    const contenidosMensual = cal.mensual.contenidos.monto;

    // Conversión de hoy por el helper único: Perú = TC mensual (`toPEN`); Chile = factory bound con la fecha puntual.
    const montoGestion = (monto, moneda, mes1, fechaOverride) => {
      const convertido = convertirMontoCarteleria(
        pais === "Perú"
          ? (m, desde) => toPEN(m, desde, mes1 - 1)
          : (m, desde) => convertirAMonedaLocal(m, desde, pais, negocio.id, mes1, ano, fechaOverride),
        monto,
        moneda,
        local
      );
      // Chile: la factory redondea también la moneda local (`Math.round`); Perú la deja tal cual.
      return pais === "Perú" ? convertido : Math.round(convertido);
    };

    const push = (fecha, mesIndice, montoLocal, concepto) => {
      registros.push(
        crearRegistroFinancieroER({
          fecha,
          ano,
          mesIndice,
          montoLocal,
          bloque: "C",
          concepto,
          pais,
          meta: { id: negocio.id, origen: "negocios_carteleria" },
        })
      );
    };
    const monedaContenidos = () =>
      pais === "Perú" ? negocio.moneda_contenidos || negocio.moneda_gestion : negocio.moneda_contenidos;

    // Con facturas del año: solo los meses facturados (mes de la FACTURA) — D11, igual que antes.
    const cuotasFacturadas = cuotasFacturadasPorMes(negocio, ano, cal);
    if (cuotasFacturadas) {
      cuotasFacturadas.forEach((slot, m) => {
        if (slot.gestion === 0 && slot.contenidos === 0) return;
        const montoSlotGestion = totalContrato ? slot.monto : gestionMensual * slot.gestion;
        if (slot.gestion > 0 && montoSlotGestion > 0) {
          push(
            slot.fechaFactura,
            m,
            montoGestion(montoSlotGestion, negocio.moneda_gestion, m + 1, slot.fechaFactura),
            "ingreso_gestion"
          );
        }
        if (slot.contenidos > 0 && contenidosMensual > 0) {
          push(
            slot.fechaFactura,
            m,
            montoGestion(contenidosMensual * slot.contenidos, monedaContenidos(), m + 1, slot.fechaFactura),
            "ingreso_contenidos"
          );
        }
      });
      return;
    }

    // Sin facturas del año: mes a mes según el devengo del motor (fin de contrato, prórroga, ventana de contenidos).
    const porMes = (componente) => {
      const mapa = new Map();
      cal.devengo.filter((d) => d.componente === componente && d.ano === ano).forEach((d) => mapa.set(d.mesNum - 1, d));
      return mapa;
    };
    const gestion = porMes("gestion");
    const contenidos = porMes("contenidos");
    for (let m = 0; m < 12; m++) {
      const ultimoDiaMes = new Date(ano, m + 1, 0);
      const fechaParaUF = ultimoDiaMes.toISOString().split("T")[0];
      if (gestion.has(m)) {
        push(
          null,
          m,
          montoGestion(gestion.get(m).monto, negocio.moneda_gestion, m + 1, fechaParaUF),
          "ingreso_gestion"
        );
      }
      if (contenidos.has(m)) {
        push(
          null,
          m,
          montoGestion(contenidos.get(m).monto, monedaContenidos(), m + 1, fechaParaUF),
          "ingreso_contenidos"
        );
      }
    }
  });
  return registros;
};
